import React from "react";
import LegalShell, { Section } from "@/pages/LegalShell";

export default function PrivacyPage() {
    return (
        <LegalShell
            overline="Confidentialité"
            title="Politique de confidentialité."
            subtitle="Cette politique décrit comment Renforteo collecte, utilise et protège vos données personnelles, conformément au Règlement (UE) 2016/679 (RGPD) et à la loi Informatique et Libertés."
        >
            <Section title="Responsable du traitement" id="responsable">
                <p>
                    Le responsable du traitement est la société <b>Renforteo</b>, dont le siège
                    social est situé au 41 rue Jean Moulin, 69300 Caluire-et-Cuire.
                    Pour toute question relative à vos données, écrivez à{" "}
                    <a href="mailto:contact@renforteo.fr" className="text-[#F5A623]">
                        contact@renforteo.fr
                    </a>.
                </p>
            </Section>

            <Section title="Données collectées" id="donnees">
                <p>Nous collectons uniquement les données strictement nécessaires au service :</p>
                <ul className="list-disc pl-6 space-y-1.5">
                    <li><b>Identification</b> : nom, prénom, e-mail, téléphone, date de naissance (candidats)</li>
                    <li><b>Adresse</b> : postale et professionnelle, géolocalisation dérivée (latitude / longitude) pour le matching</li>
                    <li><b>Documents justificatifs</b> (candidats) : pièce d'identité, RIB, justificatif de domicile, diplômes selon métier</li>
                    <li><b>Données professionnelles</b> : métier, expériences, compétences, disponibilités, tarifs</li>
                    <li><b>Entreprise</b> (clients) : raison sociale, SIRET, adresse de facturation, numéro de TVA</li>
                    <li><b>Données d'usage</b> : missions publiées, candidatures, contrats, feuilles d'heures, historique de facturation, messages échangés</li>
                </ul>
            </Section>

            <Section title="Finalités et base légale" id="finalites">
                <ul className="list-disc pl-6 space-y-1.5">
                    <li>
                        <b>Exécution du contrat</b> — mise en relation, gestion des missions,
                        facturation, contrats indépendants (art. 6-1-b RGPD)
                    </li>
                    <li>
                        <b>Obligations légales</b> — conservation des factures, pièces
                        comptables, documents fiscaux et de facturation (art. 6-1-c RGPD)
                    </li>
                    <li>
                        <b>Intérêt légitime</b> — sécurité de la plateforme, prévention de la
                        fraude, statistiques anonymisées (art. 6-1-f RGPD)
                    </li>
                    <li>
                        <b>Consentement</b> — envoi de notifications par e-mail, sollicitation
                        commerciale ponctuelle (retirable à tout moment)
                    </li>
                </ul>
            </Section>

            <Section title="Destinataires" id="destinataires">
                <p>
                    Vos données ne sont accessibles qu'aux personnes strictement habilitées :
                </p>
                <ul className="list-disc pl-6 space-y-1.5">
                    <li>Équipe Renforteo (vérification, support, comptabilité)</li>
                    <li>Client sélectionnant un professionnel pour sa mission — coordonnées et documents prestataire (KBIS, attestation de vigilance) strictement nécessaires uniquement</li>
                    <li>Professionnel sélectionné — coordonnées du client uniquement après validation</li>
                    <li>Sous-traitants techniques : hébergement, envoi d'e-mails (Resend), paiement (Stripe), stockage documentaire — sous contrat de sous-traitance conforme à l'article 28 RGPD</li>
                </ul>
                <p>
                    Aucune donnée n'est vendue, louée ou cédée à des tiers à des fins commerciales.
                </p>
            </Section>

            <Section title="Transferts hors UE" id="transferts">
                <p>
                    Les données sont hébergées au sein de l'Union européenne. Les rares
                    sous-traitants situés hors UE (fournisseurs cloud utilisés par nos prestataires
                    techniques) bénéficient soit d'une décision d'adéquation de la Commission
                    européenne, soit de clauses contractuelles types (CCT).
                </p>
            </Section>

            <Section title="Durée de conservation" id="conservation">
                <ul className="list-disc pl-6 space-y-1.5">
                    <li>Compte utilisateur : durée de la relation contractuelle + 3 ans après dernière activité</li>
                    <li>Documents justificatifs : durée de la relation + délais légaux (10 ans, obligations fiscales et comptables)</li>
                    <li>Factures et données comptables : 10 ans (obligation légale)</li>
                    <li>Journaux de connexion : 12 mois</li>
                    <li>Cookies techniques : 13 mois maximum</li>
                </ul>
            </Section>

            <Section title="Vos droits" id="droits">
                <p>
                    Vous disposez, à tout moment, des droits suivants sur vos données :
                </p>
                <ul className="list-disc pl-6 space-y-1.5">
                    <li><b>Accès</b> : obtenir une copie de vos données</li>
                    <li><b>Rectification</b> : corriger une donnée inexacte</li>
                    <li><b>Effacement</b> : supprimer votre compte et vos données, sauf obligations légales de conservation</li>
                    <li><b>Portabilité</b> : recevoir vos données dans un format structuré</li>
                    <li><b>Limitation</b> et <b>opposition</b> au traitement</li>
                    <li><b>Retrait du consentement</b> pour les envois marketing</li>
                </ul>
                <p>
                    Pour exercer vos droits, écrivez à{" "}
                    <a href="mailto:contact@renforteo.fr" className="text-[#F5A623]">
                        contact@renforteo.fr
                    </a>{" "}
                    en précisant votre demande et en joignant une pièce d'identité. Nous
                    répondons dans un délai maximum d'un mois.
                </p>
                <p>
                    Vous pouvez également introduire une réclamation auprès de la CNIL
                    (3 place de Fontenoy, 75007 Paris — cnil.fr).
                </p>
            </Section>

            <Section title="Sécurité" id="securite">
                <p>
                    Nous mettons en œuvre des mesures techniques et organisationnelles pour
                    protéger vos données : chiffrement TLS des échanges, stockage chiffré des
                    documents sensibles, contrôle strict des accès, journalisation, sauvegardes
                    régulières et sensibilisation continue de notre équipe.
                </p>
            </Section>

            <Section title="Mise à jour" id="maj">
                <p>
                    Cette politique peut être mise à jour pour refléter les évolutions du
                    service ou du cadre réglementaire. Toute modification substantielle vous
                    est notifiée par e-mail au moins 30 jours avant son entrée en vigueur.
                </p>
            </Section>
        </LegalShell>
    );
}
